import { User as FirebaseUser, signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider, UserCredential, signInWithCredential, OAuthProvider } from 'firebase/auth';
import { log } from './production-logger';
import { UserProfile } from '../types/user';
import {
  registerUser,
  signInUser,
  signOutUser,
  resetPassword,
  getUserData,
} from '../firebase/auth';
import { getUserAuth, getUserProfile } from '../utils/expo-auth-persistence';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { logEvent } from '../utils/analytics';
import loggingService from './logging-service';
import { auth, firestore } from '../firebase/firebase';
import { Platform } from 'react-native';

// Define AuthResult interface
interface AuthResult {
  success: boolean;
  message: string;
  user?: FirebaseUser;
  userData?: UserProfile;
  isNewUser?: boolean;
}

// Try to import Google Sign-In for native platforms
interface GoogleSignInModule {
  configure: (config: Record<string, unknown>) => void;
  hasPlayServices: (options?: Record<string, unknown>) => Promise<void>;
  signIn: () => Promise<{ idToken: string }>;
}

interface StatusCodes {
  SIGN_IN_CANCELLED: string;
  IN_PROGRESS: string;
  PLAY_SERVICES_NOT_AVAILABLE: string;
}

let GoogleSignin: GoogleSignInModule | null = null;
let statusCodes: StatusCodes | null = null;

if (Platform.OS !== 'web') {
  try {
    const googleSigninModule = require('@react-native-google-signin/google-signin');
    GoogleSignin = googleSigninModule.GoogleSignin;
    statusCodes = googleSigninModule.statusCodes;
    
    // Configure Google Sign-In
    GoogleSignin.configure({
      webClientId: process.env.EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID,
      iosClientId: process.env.EXPO_PUBLIC_GOOGLE_IOS_CLIENT_ID,
      offlineAccess: false,
    });
  } catch (error) {
    log.warn('Google Sign-In SDK not available for native platform');
  }
}

// Try to import Apple Authentication for iOS
interface AppleAuthenticationModule {
  isAvailableAsync: () => Promise<boolean>;
  signInAsync: (options: Record<string, unknown>) => Promise<{
    identityToken: string;
    authorizationCode?: string;
    fullName?: {
      givenName?: string;
      familyName?: string;
    };
  }>;
  AppleAuthenticationScope: {
    FULL_NAME: string;
    EMAIL: string;
  };
}

let AppleAuthentication: AppleAuthenticationModule | null = null;

if (Platform.OS === 'ios') {
  try {
    AppleAuthentication = require('expo-apple-authentication');
  } catch (error) {
    log.warn('Apple Authentication SDK not available for iOS');
  }
}

/**
 * Get the currently authenticated user
 * @returns Current Firebase user or null if not authenticated
 */
export const getCurrentUser = (): FirebaseUser | null => {
  try {
    const firebaseUser = auth.currentUser;
    if (firebaseUser) {
      // For debugging - log the current user's ID and organization access
      log.debug(`Auth service: Current user ID: ${firebaseUser.uid}`);
      return firebaseUser;
    }

    // If Firebase auth doesn't have a user, this might be due to timing
    log.debug('Auth service: No Firebase user, recommend using useAuth() hook');
    return null;
  } catch (error) {
    // Firebase might not be ready yet during app initialization
    log.debug('Auth service: Error getting current user:', error);
    return null;
  }
};

/**
 * Check if a user is authenticated - helper for services
 * @returns boolean indicating if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  const currentUser = getCurrentUser();
  const isValid = !!currentUser && !!currentUser.uid;
  log.debug(`Auth service: isAuthenticated check - ${isValid}`);
  return isValid;
};

/**
 * Authentication service for handling user authentication
 */
class AuthService {
  /**
   * Register a new user with email and password
   * @param email - User's email
   * @param password - User's password
   * @param displayName - User's display name
   * @returns Promise resolving to the user credential
   */
  async register(email: string, password: string, displayName: string) {
    try {
      const userCredential = await registerUser(email, password, displayName);
      return {
        success: true,
        user: userCredential.user,
        message: 'Registration successful',
      };
    } catch (error) {
      log.error('Registration error:', error);
      return {
        success: false,
        message: this.getErrorMessage(error),
      };
    }
  }

  /**
   * Sign in a user with email and password
   * @param email - User's email
   * @param password - User's password
   * @returns Promise resolving to the user credential
   */
  async login(email: string, password: string) {
    try {
      loggingService.logUserAction('Login attempt', { email });
      const userCredential = await signInUser(email, password);

      // Get user data
      const userData = await getUserData(userCredential.user);

      // Log organization ID for debugging
      if (userData) {
        loggingService.info('Login successful', {
          userId: userCredential.user.uid,
          organizationId: userData.organizationId || 'none',
        });

        // Check if this is the specific organization with issues
        const targetOrgId = 'uxyB4jrIjxWHhuFtxVbO7fVjH3z1';
        if (userData.organizationId === targetOrgId) {
          loggingService.debug(`User belongs to target organization ${targetOrgId}`);
        }
      }

      return {
        success: true,
        user: userCredential.user,
        userData,
        message: 'Login successful',
      };
    } catch (error) {
      loggingService.error('Login failed', error, { email });
      return {
        success: false,
        message: this.getErrorMessage(error),
      };
    }
  }

  /**
   * Sign out the current user
   * @returns Promise resolving when sign out is complete
   */
  async logout() {
    try {
      loggingService.logUserAction('Logout');
      await signOutUser();
      loggingService.info('Logout successful');
      return {
        success: true,
        message: 'Logout successful',
      };
    } catch (error) {
      loggingService.error('Logout failed', error);
      return {
        success: false,
        message: this.getErrorMessage(error),
      };
    }
  }

  /**
   * Sign out the current user (alias for logout)
   * @returns Promise resolving when sign out is complete
   */
  async signOut() {
    return this.logout();
  }

  /**
   * Send password reset email
   * @param email - User's email
   * @returns Promise resolving when the reset email is sent
   */
  async forgotPassword(email: string) {
    try {
      await resetPassword(email);
      return {
        success: true,
        message: 'Password reset email sent',
      };
    } catch (error) {
      log.error('Password reset error:', error);
      return {
        success: false,
        message: this.getErrorMessage(error),
      };
    }
  }

  /**
   * Get user data from Firestore
   * @param user - Firebase User object
   * @returns Promise resolving to user data
   */
  async getUserProfile(user: FirebaseUser) {
    try {
      const userData = await getUserData(user);
      return {
        success: true,
        userData,
      };
    } catch (error) {
      log.error('Get user profile error:', error);
      return {
        success: false,
        message: this.getErrorMessage(error),
      };
    }
  }

  /**
   * Sign in with Google
   * @returns Promise resolving to authentication result
   */
  async signInWithGoogle(): Promise<AuthResult> {
    try {
      const userCredential = await signInWithGoogle();
      
      // Check if this is a new user
      const userDoc = await getDoc(doc(firestore, 'users', userCredential.user.uid));
      const isNewUser = !userDoc.exists();
      
      // Ensure user profile exists
      await ensureUserProfile(userCredential.user, 'google');
      
      // Get user profile
      const profileResult = await this.getUserProfile(userCredential.user);
      
      return {
        success: true,
        message: isNewUser ? 'Account created successfully' : 'Logged in successfully',
        user: userCredential.user,
        isNewUser,
        userData: profileResult.success ? profileResult.userData : null,
      };
    } catch (error) {
      log.error('Google sign-in error:', error);
      
      // Handle specific Google sign-in errors
      if (error.code === 'auth/popup-closed-by-user') {
        return {
          success: false,
          message: 'Sign-in cancelled',
        };
      }
      
      return {
        success: false,
        message: this.getErrorMessage(error),
      };
    }
  }

  /**
   * Sign in with Apple
   * @returns Promise resolving to authentication result
   */
  async signInWithApple(): Promise<AuthResult> {
    try {
      const userCredential = await signInWithApple();
      
      // Check if this is a new user
      const userDoc = await getDoc(doc(firestore, 'users', userCredential.user.uid));
      const isNewUser = !userDoc.exists();
      
      // Ensure user profile exists
      await ensureUserProfile(userCredential.user, 'apple');
      
      // Get user profile
      const profileResult = await this.getUserProfile(userCredential.user);
      
      return {
        success: true,
        message: isNewUser ? 'Account created successfully' : 'Logged in successfully',
        user: userCredential.user,
        isNewUser,
        userData: profileResult.success ? profileResult.userData : null,
      };
    } catch (error) {
      log.error('Apple sign-in error:', error);
      
      // Handle specific Apple sign-in errors
      if (error.code === 'ERR_CANCELED') {
        return {
          success: false,
          message: 'Sign-in cancelled',
        };
      }
      
      return {
        success: false,
        message: this.getErrorMessage(error),
      };
    }
  }

  /**
   * Get readable error message from Firebase error
   * @param error - Firebase error
   * @returns Readable error message
   */
  private getErrorMessage(error: unknown): string {
    const errorObj = error as { code?: string; message?: string };
    const errorCode = errorObj.code;

    switch (errorCode) {
      case 'auth/email-already-in-use':
        return 'This email is already registered. Please login or use a different email.';
      case 'auth/invalid-email':
        return 'The email address is not valid.';
      case 'auth/user-disabled':
        return 'This account has been disabled.';
      case 'auth/user-not-found':
        return 'No account found with this email. Please register first.';
      case 'auth/wrong-password':
        return 'Incorrect password. Please try again.';
      case 'auth/weak-password':
        return 'Password is too weak. Please use a stronger password.';
      case 'auth/operation-not-allowed':
        return 'This operation is not allowed.';
      case 'auth/too-many-requests':
        return 'Too many unsuccessful attempts. Please try again later.';
      default:
        return errorObj.message || 'An unknown error occurred. Please try again.';
    }
  }
}

export default new AuthService();

/**
 * Ensure the user has a profile in Firestore
 * @param user - The Firebase user
 * @param authProvider - The authentication provider used
 */
async function ensureUserProfile(user: FirebaseUser, authProvider?: 'email' | 'google' | 'apple'): Promise<void> {
  if (!user || !user.uid) return;

  try {
    // Direct usage of firestore instance
    const userRef = doc(firestore, 'users', user.uid);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      log.debug(`Creating user profile for ${user.uid}`);

      await setDoc(userRef, {
        email: user.email || '',
        displayName: user.displayName || '',
        photoURL: user.photoURL || '',
        authProvider: authProvider || 'email',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastLogin: serverTimestamp(),
      });

      logEvent('user_profile_created', { userId: user.uid, authProvider });

      // Send welcome email for new users (except in demo mode)
      if (user.email && process.env.EXPO_PUBLIC_SENDGRID_API_KEY) {
        try {
          // Dynamic import to avoid circular dependencies
          const { emailService } = await import('./email-service');
          await emailService.sendWelcomeEmail(
            user.email,
            user.displayName || 'there',
            undefined // No organization yet
          );
        } catch (emailError) {
          log.warn('Failed to send welcome email:', emailError);
          // Don't fail user creation if email fails
        }
      }
    } else {
      // Update last login timestamp and auth provider if changed
      const updates: Record<string, unknown> = {
        lastLogin: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      
      if (authProvider) {
        updates.authProvider = authProvider;
      }
      
      await updateDoc(userRef, updates);
    }
  } catch (error) {
    log.error('Error ensuring user profile:', error);
  }
}

/**
 * Sign in with email and password
 * @param email - User email
 * @param password - User password
 * @returns Promise with user credentials
 */
export async function signInWithEmailPassword(
  email: string,
  password: string,
): Promise<UserCredential> {
  // Direct usage of auth instance
  
  // Sign in with Firebase Authentication
  const userCredential = await signInWithEmailAndPassword(auth, email, password);

  // Ensure user profile and organization structure exist
  await ensureUserProfile(userCredential.user, 'email');

  // Log analytics event
  logEvent('login', { method: 'email' });

  return userCredential;
}

/**
 * Sign in with Google
 * @returns Promise with user credentials
 */
export async function signInWithGoogle(): Promise<UserCredential> {
  try {
    let userCredential: UserCredential;

    if (Platform.OS === 'web') {
      // Web implementation using Firebase popup
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      userCredential = await signInWithPopup(auth, provider);
    } else {
      // Native implementation using Google Sign-In SDK
      if (!GoogleSignin) {
        throw new Error('Google Sign-In is not configured');
      }

      // Check if device has Google Play Services
      await GoogleSignin.hasPlayServices({ showPlayServicesUpdateDialog: true });

      // Get the user's ID token
      const { idToken } = await GoogleSignin.signIn();

      // Create a Google credential with the token
      const googleCredential = GoogleAuthProvider.credential(idToken);

      // Sign-in the user with the credential
      userCredential = await signInWithCredential(auth, googleCredential);
    }

    // Ensure user profile and organization structure exist
    await ensureUserProfile(userCredential.user, 'google');

    // Log analytics event
    logEvent('login', { method: 'google' });

    return userCredential;
  } catch (error) {
    // Handle Google Sign-In specific errors for native
    if (Platform.OS !== 'web' && statusCodes) {
      if (error.code === statusCodes.SIGN_IN_CANCELLED) {
        throw new Error('auth/popup-closed-by-user');
      } else if (error.code === statusCodes.IN_PROGRESS) {
        throw new Error('Sign-in already in progress');
      } else if (error.code === statusCodes.PLAY_SERVICES_NOT_AVAILABLE) {
        throw new Error('Google Play Services not available');
      }
    }
    throw error;
  }
}

/**
 * Sign in with Apple
 * @returns Promise with user credentials
 */
export async function signInWithApple(): Promise<UserCredential> {
  try {
    let userCredential: UserCredential;

    if (Platform.OS === 'ios') {
      // iOS implementation using expo-apple-authentication
      if (!AppleAuthentication) {
        throw new Error('Apple Sign-In is not available on this device');
      }

      // Check if Apple Authentication is available
      const isAvailable = await AppleAuthentication.isAvailableAsync();
      if (!isAvailable) {
        throw new Error('Apple Sign-In is not available on this device');
      }

      // Request Apple ID credentials
      const credential = await AppleAuthentication.signInAsync({
        requestedScopes: [
          AppleAuthentication.AppleAuthenticationScope.FULL_NAME,
          AppleAuthentication.AppleAuthenticationScope.EMAIL,
        ],
      });

      // Create an OAuth provider credential
      const provider = new OAuthProvider('apple.com');
      const oAuthCredential = provider.credential({
        idToken: credential.identityToken!,
        rawNonce: credential.authorizationCode,
      });

      // Sign in with Firebase
      userCredential = await signInWithCredential(auth, oAuthCredential);

      // If we got a full name from Apple, update the user profile
      if (credential.fullName && userCredential.user) {
        const displayName = [credential.fullName.givenName, credential.fullName.familyName]
          .filter(Boolean)
          .join(' ');
        
        if (displayName) {
          // Update the user profile with the display name
          const userRef = doc(firestore, 'users', userCredential.user.uid);
          await updateDoc(userRef, {
            displayName,
            updatedAt: serverTimestamp(),
          });
        }
      }
    } else {
      // Web/Android not supported
      throw new Error('Apple Sign-In is only available on iOS devices');
    }

    // Ensure user profile and organization structure exist
    await ensureUserProfile(userCredential.user, 'apple');

    // Log analytics event
    logEvent('login', { method: 'apple' });

    return userCredential;
  } catch (error) {
    // Handle Apple Authentication specific errors
    if (error.code === 'ERR_CANCELED') {
      throw new Error('auth/popup-closed-by-user');
    }
    throw error;
  }
}
